import { useEffect, useState } from "react";
import { notificationsApi } from "../services/notificationsApi";
import { authApi } from "../services/authApi";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { Spinner } from "../components/common/Spinner";

const QUIET_HOURS_KEY = "beekeeper.quiet_hours";

function loadQuietHours() {
  try {
    return JSON.parse(localStorage.getItem(QUIET_HOURS_KEY) || "null") || { enabled: false, start: "21:00", end: "07:00" };
  } catch {
    return { enabled: false, start: "21:00", end: "07:00" };
  }
}

export function NotificationsPage() {
  const { deviceFingerprint, deviceName } = useAuth();
  const { notify } = useToast();
  const [briefing, setBriefing] = useState(null);
  const [isTrusted, setIsTrusted] = useState(true);
  const [quietHours, setQuietHours] = useState(loadQuietHours);

  // Was an unguarded Promise.all with `if (!briefing) return <Spinner />`
  // below, so a failure in EITHER call left the whole page spinning
  // forever with nothing on screen to say why.
  const [loadError, setLoadError] = useState(null);

  async function load() {
    const [briefingRes, devicesRes] = await Promise.allSettled([
      notificationsApi.todayBriefing(),
      authApi.trustedDevices(),
    ]);

    if (briefingRes.status === "fulfilled") {
      setBriefing(briefingRes.value);
      setLoadError(null);
    } else {
      const status = briefingRes.reason?.response?.status;
      setLoadError(
        status === 403
          ? "Daily briefings aren't included on your current plan."
          : "Couldn't load your briefing.",
      );
    }

    // The trusted-device check is secondary -- it decides one banner. It
    // must never be able to hide the whole page, so a failure here just
    // leaves the banner alone.
    if (devicesRes.status === "fulfilled") {
      setIsTrusted((devicesRes.value.trusted_devices || []).some((d) => d.device_fingerprint === deviceFingerprint));
    }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function snooze(type) {
    try {
      await notificationsApi.snooze(type, 1440);
      notify("Snoozed for 24 hours", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not snooze", "danger");
    }
  }

  async function trustThisDevice() {
    try {
      await authApi.trustDevice(deviceName, deviceFingerprint, 90);
      setIsTrusted(true);
      notify("Device trusted", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not trust device", "danger");
    }
  }

  function saveQuietHours(next) {
    setQuietHours(next);
    localStorage.setItem(QUIET_HOURS_KEY, JSON.stringify(next));
  }

  if (loadError) {
    return (
      <div>
        <h1>Notifications</h1>
        <Card>
          <p>{loadError}</p>
          <Button onClick={load}>Try again</Button>
        </Card>
      </div>
    );
  }
  if (!briefing) return <Spinner />;

  return (
    <div>
      <h1>Notifications</h1>

      {!isTrusted && (
        <Card title="🔔 Security Notification" className="section-spacing">
          <p><Badge tone="warning">New device detected</Badge> This device isn't trusted yet. Was this you?</p>
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <Button onClick={trustThisDevice}>Yes, trust this device</Button>
            <Button variant="danger" onClick={() => notify("Go to Settings → Security Center to revoke sessions if this wasn't you.", "warning")}>
              This wasn't me
            </Button>
          </div>
        </Card>
      )}

      {briefing.hives_due && (
        <Card title="Hives Due" actions={<Button size="sm" variant="secondary" onClick={() => snooze("hives_due")}>Snooze 24h</Button>} className="section-spacing">
          {briefing.hives_due.length === 0 ? <p>All caught up!</p> : (
            <ul>{briefing.hives_due.map((h) => <li key={h.hive_id}>Hive {h.hive_number}</li>)}</ul>
          )}
        </Card>
      )}

      {briefing.low_health_hives && (
        <Card title="Low Health Alerts" actions={<Button size="sm" variant="secondary" onClick={() => snooze("health_alerts")}>Snooze 24h</Button>} className="section-spacing">
          <ul>{briefing.low_health_hives.map((h) => <li key={h.hive_id}>Hive {h.hive_number} — score {h.score}</li>)}</ul>
        </Card>
      )}

      {briefing.tasks_due_today && (
        <Card title="Tasks Due Today" actions={<Button size="sm" variant="secondary" onClick={() => snooze("team_tasks")}>Snooze 24h</Button>} className="section-spacing">
          <ul>{briefing.tasks_due_today.map((t) => <li key={t.task_id}>{t.task_type}</li>)}</ul>
        </Card>
      )}

      {briefing.bloom_starting_soon && (
        <Card title="Bloom Starting Soon" actions={<Button size="sm" variant="secondary" onClick={() => snooze("bloom_alerts")}>Snooze 24h</Button>} className="section-spacing">
          <ul>{briefing.bloom_starting_soon.map((b) => <li key={b.plant_id}>{b.plant_name}</li>)}</ul>
        </Card>
      )}

      <Card title="Quiet Hours">
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          Stored locally in this browser — there's no backend field for quiet hours yet, so this doesn't suppress
          server-sent push notifications (only in-app display timing here).
        </p>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <input id="notifications-page-enabled" name="enabled" type="checkbox" checked={quietHours.enabled} onChange={(e) => saveQuietHours({ ...quietHours, enabled: e.target.checked })} />
          Enable quiet hours
        </label>
        <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-2)" }}>
          <label>From <input id="notifications-page-start" name="start" type="time" value={quietHours.start} onChange={(e) => saveQuietHours({ ...quietHours, start: e.target.value })} className="touch-target" /></label>
          <label>To <input id="notifications-page-end" name="end" type="time" value={quietHours.end} onChange={(e) => saveQuietHours({ ...quietHours, end: e.target.value })} className="touch-target" /></label>
        </div>
      </Card>

      <FeatureDiscoveryEmailToggle />
    </div>
  );
}

const DISCOVERY_EMAIL_KEY = "beekeeper.feature_discovery_email";

/**
 * Disabled by default, per spec. Stored locally only -- Users.feature_preferences
 * (a JSON column already in the schema) would be the natural server-side
 * home for this, but no endpoint reads/writes it today, and there's no
 * email-sending job wired to check it either. This toggle currently has no
 * effect beyond remembering the user's choice in this browser.
 */
function FeatureDiscoveryEmailToggle() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(DISCOVERY_EMAIL_KEY) === "true");

  function toggle(value) {
    localStorage.setItem(DISCOVERY_EMAIL_KEY, String(value));
    setEnabled(value);
  }

  return (
    <Card title="Feature Discovery Email" className="section-spacing">
      <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        Monthly summary of features you haven't tried yet. Disabled by default. (Preference saved locally only — no
        backend field or email job exists yet to actually send this.)
      </p>
      <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <input id="notifications-page-enabled-2" name="enabled" type="checkbox" checked={enabled} onChange={(e) => toggle(e.target.checked)} />
        Send me the monthly feature discovery email
      </label>
    </Card>
  );
}
